import { autoinject, bindingMode, noView } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ITrackAuth, TrackingService, ITrackingQueue } from 'app/um/services/tracking-service';
import { TickService, ITickEvent, TickHandler } from 'app/shared/services/event-services';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { State } from 'app/shared/lib/state';
import { bindable } from 'aurelia-typed-observable-plugin';
import * as UM from 'app/um/models/essette-api';

export interface TrackingQueue {
    id?: string | null;
    auths?: Array<UM.Auth>;
    queueType?: string | null;
}

@autoinject
@noView
export class QueueUserTracker {
    @bindable.object({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected authQueue: TrackingQueue | null = null;

    @bindable.object({ defaultBindingMode: bindingMode.fromView })
    protected trackedAuths: ITrackAuth[] = [];

    protected readonly state: State = new State();

    private lastUpdate: number;
    private readonly updateDuration: number;
    private readonly updateUsers: TickHandler;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly env: EnvironmentService,
        private readonly ticker: TickService,
        private readonly trackingService: TrackingService,
        private readonly logger: AppLogger) {
        this.logger.source = this;

        this.updateDuration = this.env.config.tracking.updateIntervalSeconds * 1000; // seconds to milliseconds
        this.lastUpdate = 0; // trigger update immediately

        this.updateUsers = (e: ITickEvent) => {
            if(e.time > this.lastUpdate + this.updateDuration) {
                if(!env.user.isLoggedIn) {
                    this.reset(false);
                }
                else if(!this.state.busy && env.isAppVisible()) {
                    this.track();
                }
            }
        }

    }

    protected bind(): void {
        this.ticker.addEventListener('tick', this.updateUsers);
    }

    protected unbind(): void {
        this.ticker.removeEventListener('tick', this.updateUsers);
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        this.track();
    }

    ////****************** Functions ************************************************////
    // Reset tracker
    private reset(load: boolean = true): void {
        this.lastUpdate = 0; // trigger update immediately
        if(load) {
            this.track();
        }
    }

    // Track Current Users in Auths
    private async track(): Promise<void> {
        if(this.authQueue?.auths && this.authQueue?.auths.length < 1) {
            this.trackedAuths = [];
        }
        else if(this.authQueue?.id && this.authQueue.auths) {
            const auths = this.authQueue.auths.map(a => {
                return { id: a.id };
            });

            const trackingQueue: ITrackingQueue = {
                auths: auths
            }

            try {
                this.state.beginLoad();
                if(!this.env.user.isLoggedIn) { return; }
                let response;
                switch(this.authQueue.queueType) {
                    case 'portal':
                        response = await this.trackingService.trackPortalQueueUsers(this.authQueue.id, trackingQueue);
                        break;
                    case 'auth':
                    default:
                        response = await this.trackingService.trackAuthQueueUsers(this.authQueue.id, trackingQueue);
                        break;
                }

                if(response.isSuccessful && response.result?.auths) {
                    this.trackedAuths = response.result.auths;
                }
                else {
                    this.trackedAuths = [];
                }

                this.lastUpdate = Date.now();
            }
            catch(error) {
                this.logger.error('Error Tracking Auth Queue Users', error);
            }
            finally {
                this.state.endLoad();
            }
        }
    }

}
